import React from "react";
import {
  FaArrowRight,
  FaChalkboardTeacher,
  FaClock,
  FaUsers,
} from "react-icons/fa";

import {
  cn,
  fillPercentage,
  getHexColor,
  horairesOf,
  isDark,
  isGroupFull,
  lighten,
  teachersOf,
} from "./groupPlanningUtils";

export default function GroupMiniCard({ group, dojoName, onOpen }) {
  const baseColor = getHexColor(group.color_groupe);
  const lightColor = lighten(baseColor, 0.68);
  const titleColor = isDark(baseColor) ? "#ffffff" : "#0f172a";

  const horaires = horairesOf(group);
  const teachers = teachersOf(group);
  const full = isGroupFull(group);
  const percentage = fillPercentage(group);

  return (
    <article
      className="
        group relative mx-auto flex h-full w-full max-w-[320px] flex-col
        overflow-hidden rounded-[2rem]
        border border-slate-200 bg-white shadow-sm
        transition duration-300
        hover:-translate-y-2 hover:border-orange-300 hover:shadow-2xl
        dark:border-white/10 dark:bg-slate-900
        dark:hover:border-orange-500/40
      "
    >
      {/* En-tête coloré */}
      <div
        className="relative overflow-hidden p-5"
        style={{
          background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
        }}
      >
        <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/20 blur-2xl" />

        <div className="pointer-events-none absolute -bottom-10 -left-10 h-24 w-24 rounded-full bg-white/10 blur-2xl" />

        <div className="relative flex flex-wrap gap-2">
          <span className="rounded-full border border-white/30 bg-white/75 px-3 py-1 text-[11px] font-bold text-slate-900 shadow-sm backdrop-blur">
            {dojoName || "Dojo"}
          </span>

          {group.discipline_groupe && (
            <span className="rounded-full border border-white/30 bg-white/75 px-3 py-1 text-[11px] font-bold text-slate-900 shadow-sm backdrop-blur">
              {group.discipline_groupe}
            </span>
          )}

          {full && (
            <span className="rounded-full border border-rose-500 bg-rose-600 px-3 py-1 text-[11px] font-bold uppercase text-white shadow-sm">
              Complet
            </span>
          )}
        </div>

        <h4
          className="relative mt-5 font-barlow text-2xl font-extrabold uppercase leading-tight drop-shadow-sm"
          style={{ color: titleColor }}
        >
          {group.nom}
        </h4>

        {group.annees_naissance && (
          <p
            className="relative mt-2 text-sm font-bold italic"
            style={{ color: titleColor }}
          >
            {group.annees_naissance}
          </p>
        )}
      </div>

      {/* Contenu */}
      <div className="flex flex-1 flex-col p-5 text-slate-900 dark:text-white">
        <div className="grid gap-4 text-sm">
          {/* Enseignants */}
          <div className="flex items-start gap-3">
            <span
              className="
                mt-0.5 flex h-10 w-10 shrink-0
                items-center justify-center rounded-2xl
                bg-orange-100 text-orange-600
                dark:bg-orange-500/10 dark:text-orange-400
              "
            >
              <FaChalkboardTeacher />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                Enseignant(s)
              </p>

              <p className="mt-1 break-words leading-6 text-slate-600 dark:text-slate-300">
                {teachers.length ? teachers.join(" • ") : "Non renseigné"}
              </p>
            </div>
          </div>

          {/* Horaires */}
          <div className="flex items-start gap-3">
            <span
              className="
                mt-0.5 flex h-10 w-10 shrink-0
                items-center justify-center rounded-2xl
                bg-orange-100 text-orange-600
                dark:bg-orange-500/10 dark:text-orange-400
              "
            >
              <FaClock />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                Horaires
              </p>

              {horaires.length ? (
                <ul className="mt-1 space-y-1 leading-6 text-slate-600 dark:text-slate-300">
                  {horaires.slice(0, 3).map((horaire, index) => (
                    <li key={`${horaire}-${index}`} className="break-words">
                      <span className="mr-1 text-orange-500">•</span>
                      {horaire}
                    </li>
                  ))}

                  {horaires.length > 3 && (
                    <li className="text-xs italic text-slate-500 dark:text-slate-400">
                      … +{horaires.length - 3} autre
                      {horaires.length - 3 > 1 ? "s" : ""} créneau
                      {horaires.length - 3 > 1 ? "x" : ""}
                    </li>
                  )}
                </ul>
              ) : (
                <p className="mt-1 italic text-slate-500 dark:text-slate-400">
                  Non renseigné
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Effectif */}
        <div
          className="
            mt-5 rounded-3xl
            border border-slate-200 bg-slate-50 p-4
            dark:border-white/10 dark:bg-white/5
          "
        >
          <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-300">
            <span className="inline-flex items-center gap-2">
              <FaUsers className="text-orange-600 dark:text-orange-400" />
              Effectif
            </span>

            <span
              className={cn(
                percentage >= 100
                  ? "text-rose-600 dark:text-rose-400"
                  : "text-orange-600 dark:text-orange-400"
              )}
            >
              {percentage} %
            </span>
          </div>

          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className={cn(
                "h-full rounded-full transition-[width] duration-500",
                percentage >= 100
                  ? "bg-rose-500"
                  : "bg-orange-500 dark:bg-orange-400"
              )}
              style={{
                width: `${Math.min(percentage, 100)}%`,
              }}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              {group.adherentCount || 0}/{group.quota_groupe || 0} inscrit(s)
            </p>

            {full && (
              <span className="rounded-full bg-rose-100 px-2.5 py-1 text-[10px] font-bold uppercase text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                Groupe complet
              </span>
            )}
          </div>
        </div>

        {/* Bouton détails */}
        <button
          type="button"
          onClick={() => onOpen(group)}
          className="
            mt-5 inline-flex items-center justify-center gap-2
            rounded-full border border-orange-300
            bg-orange-50 px-5 py-3
            text-sm font-bold text-orange-700
            shadow-sm transition duration-200
            hover:-translate-y-0.5 hover:gap-3
            hover:border-orange-600 hover:bg-orange-600
            hover:text-white hover:shadow-lg
            focus:outline-none focus:ring-4 focus:ring-orange-500/20

            dark:border-orange-500/40
            dark:bg-orange-500/10
            dark:text-orange-300
            dark:hover:border-orange-400
            dark:hover:bg-orange-500
            dark:hover:text-white
          "
        >
          Voir les détails
          <FaArrowRight className="text-xs" />
        </button>
      </div>

      {/* Ligne décorative conservant la couleur du groupe */}
      <div
        aria-hidden="true"
        className="h-1.5 w-full opacity-70 transition duration-300 group-hover:opacity-100"
        style={{
          background: `linear-gradient(to right, transparent, ${baseColor}, transparent)`,
        }}
      />
    </article>
  );
}